import React, { useMemo, useRef, useState, useEffect } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Html } from '@react-three/drei'
import * as THREE from 'three'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Nhãn vec tơ: chữ in nghiêng + mũi tên vec tơ SVG phía trên
function VecLabel({ sym, color }: { sym: string; color: string }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', color, fontWeight: 700, fontStyle: 'italic', fontSize: 17, lineHeight: 1, paddingTop: 7, textShadow: '0 0 3px #fff, 0 0 3px #fff' }}>
      <span style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '1.3em', height: 6 }}>
        <svg viewBox="0 0 26 6" width="100%" height="6" style={{ display: 'block', overflow: 'visible' }}>
          <line x1="1" y1="3" x2="23" y2="3" stroke={color} strokeWidth="2" />
          <path d="M23 3 L18 0.6 M23 3 L18 5.4" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
      </span>
      {sym}
    </span>
  )
}
// Nhãn đỉnh khung (chữ thường, không có mũi tên)
function CornerLabel({ sym }: { sym: string }) {
  return <span style={{ color: '#1e293b', fontWeight: 800, fontSize: 15, textShadow: '0 0 3px #fff, 0 0 3px #fff' }}>{sym}</span>
}

function Arrow3({ pos, dir, len, color }: { pos: [number, number, number]; dir: [number, number, number]; len: number; color: number }) {
  const h = useMemo(() => new THREE.ArrowHelper(new THREE.Vector3(...dir).normalize(), new THREE.Vector3(...pos), len, color, 0.32, 0.16), [pos, dir, len, color])
  return <primitive object={h} />
}

function Edge({ pos, size, color }: { pos: [number, number, number]; size: [number, number, number]; color: string }) {
  return <mesh position={pos}><boxGeometry args={size} /><meshStandardMaterial color={color} metalness={0.4} roughness={0.4} /></mesh>
}

// B hướng +x (N ở −x sang S ở +x). Khung dây quay quanh trục y.
// Từ thông Φ = B·A·cos? — ở đây dùng e ∝ cos(φ): e=0 khi mặt khung ⊥ B (Φ cực đại),
// |e| cực đại khi mặt khung ∥ B (Φ=0). Chiều dòng điện đảo khi e đổi dấu.
function GeneratorScene({ omega, angleRef, eRef, histRef, manual }: { omega: number; angleRef: React.MutableRefObject<number>; eRef: React.MutableRefObject<number>; histRef: React.MutableRefObject<number[]>; manual: boolean }) {
  const coil = useRef<THREE.Group>(null)
  const arrR = useRef<THREE.Mesh>(null)
  const arrL = useRef<THREE.Mesh>(null)
  // trục đối xứng (trục quay) khung dây — đường nét đứt dọc theo y
  const axisLine = useMemo(() => {
    const g = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, -1.8, 0), new THREE.Vector3(0, 1.8, 0)])
    const l = new THREE.Line(g, new THREE.LineDashedMaterial({ color: 0x64748b, dashSize: 0.16, gapSize: 0.12 }))
    l.computeLineDistances()
    return l
  }, [])
  useFrame((_, dt) => {
    angleRef.current += omega * Math.min(0.05, dt)
    const phi = angleRef.current
    const e = Math.cos(phi)
    eRef.current = e
    const hist = histRef.current; hist.push(e); if (hist.length > 220) hist.shift()
    if (coil.current) coil.current.rotation.y = phi
    // mũi tên chiều dòng điện trên 2 cạnh tác dụng (đảo chiều theo dấu e)
    if (arrR.current) arrR.current.rotation.z = e >= 0 ? 0 : Math.PI
    if (arrL.current) arrL.current.rotation.z = e >= 0 ? Math.PI : 0
  })
  return (
    <>
      <ambientLight intensity={0.85} />
      <directionalLight position={[4, 6, 5]} intensity={0.8} />
      {/* Cực từ: N (đỏ, −x) → S (xanh, +x) */}
      <mesh position={[-2.6, 0, 0]}><boxGeometry args={[0.6, 2.6, 2.6]} /><meshStandardMaterial color="#ef4444" /></mesh>
      <mesh position={[2.6, 0, 0]}><boxGeometry args={[0.6, 2.6, 2.6]} /><meshStandardMaterial color="#3b82f6" /></mesh>
      <Html position={[-2.6, 1.6, 0]} center><span style={{ color: '#b91c1c', fontWeight: 800 }}>N</span></Html>
      <Html position={[2.6, 1.6, 0]} center><span style={{ color: '#1d4ed8', fontWeight: 800 }}>S</span></Html>
      {/* Đường sức từ (xanh lá) hướng N→S — lưới 3×3 = từ trường đều */}
      {[-0.9, 0, 0.9].flatMap((y) => [-0.8, 0, 0.8].map((z) => (
        <Arrow3 key={`${y}_${z}`} pos={[-2.2, y, z]} dir={[1, 0, 0]} len={4.4} color={0x16a34a} />
      )))}
      <Html position={[0, 0.35, 1.1]} center><VecLabel sym="B" color="#16a34a" /></Html>

      {/* Trục đối xứng / trục quay (nét đứt) */}
      <primitive object={axisLine} />

      {/* KHUNG DÂY CHỮ NHẬT (quay quanh y) */}
      <group ref={coil}>
        {/* 2 cạnh tác dụng (dọc theo y) — đổi màu */}
        <Edge pos={[1, 0, 0]} size={[0.1, 2.1, 0.1]} color="#f59e0b" />
        <Edge pos={[-1, 0, 0]} size={[0.1, 2.1, 0.1]} color="#10b981" />
        {/* cạnh trên/dưới (dọc theo x) */}
        <Edge pos={[0, 1, 0]} size={[2.1, 0.1, 0.1]} color="#0f766e" />
        <Edge pos={[0, -1, 0]} size={[2.1, 0.1, 0.1]} color="#0f766e" />
        {/* mũi tên chiều dòng điện */}
        <mesh ref={arrR} position={[1, 0, 0]}><coneGeometry args={[0.12, 0.34, 12]} /><meshStandardMaterial color="#b45309" /></mesh>
        <mesh ref={arrL} position={[-1, 0, 0]}><coneGeometry args={[0.12, 0.34, 12]} /><meshStandardMaterial color="#065f46" /></mesh>
        {/* Vec tơ pháp tuyến n (vuông góc mặt khung, +z) — quay theo khung */}
        <Arrow3 pos={[0, 0, 0]} dir={[0, 0, 1]} len={1.4} color={0x7c3aed} />
        <Html position={[0, 0.18, 1.65]} center><VecLabel sym="n" color="#7c3aed" /></Html>
        {/* Nhãn 4 đỉnh khung dây A, B, C, D */}
        <Html position={[-1.2, 1.2, 0]} center><CornerLabel sym="A" /></Html>
        <Html position={[1.2, 1.2, 0]} center><CornerLabel sym="B" /></Html>
        <Html position={[1.2, -1.2, 0]} center><CornerLabel sym="C" /></Html>
        <Html position={[-1.2, -1.2, 0]} center><CornerLabel sym="D" /></Html>
      </group>
      <OrbitControls enablePan={false} enableRotate={!manual} />
    </>
  )
}

const AcGeneratorSimulation: React.FC<Props> = ({
  title = 'Máy phát điện xoay chiều (3D)',
  description = 'Khung dây quay đều trong từ trường B (N→S) → suất điện động e = E₀·cos(ωt). Chiều dòng điện trong khung đảo mỗi nửa vòng. Kéo chuột để xoay góc nhìn.',
  onComplete,
}) => {
  const [omega, setOmega] = useState(2)
  const [running, setRunning] = useState(true)
  const [manual, setManual] = useState(false)   // quay tay (thanh trượt/kéo) thay vì tự động
  const [manualDeg, setManualDeg] = useState(0) // góc khung khi quay tay (độ)
  const dragRef = useRef(false)
  const angleRef = useRef(0)
  const eRef = useRef(1)
  const histRef = useRef<number[]>([])
  const gcv = useRef<HTMLCanvasElement>(null)
  const graphRaf = useRef<number | null>(null)
  const firstRef = useRef(false)
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }
  const onDown = () => { if (manual) { dragRef.current = true; touch() } }
  const onMove = (e: React.PointerEvent) => { if (dragRef.current && manual) { angleRef.current += e.movementX * 0.02; setManualDeg(((angleRef.current * 180 / Math.PI) % 360 + 360) % 360) } }
  const onUp = () => { dragRef.current = false }

  // Đồ thị e(t) 2D phía dưới (đọc histRef do useFrame cập nhật)
  useEffect(() => {
    const loop = () => {
      const ctx = prepCanvas(gcv.current, 660, 90)
      if (ctx) {
        const W = 660, H = 90, y0 = H / 2
        ctx.clearRect(0, 0, W, H)
        ctx.strokeStyle = '#cbd5e1'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(0, y0); ctx.lineTo(W, y0); ctx.stroke()
        ctx.fillStyle = '#475569'; ctx.font = 'bold 12px sans-serif'; ctx.fillText('e', 4, 14)
        const hist = histRef.current
        ctx.strokeStyle = '#dc2626'; ctx.lineWidth = 2.5; ctx.beginPath()
        hist.forEach((e, i) => { const px = (i / 220) * W, py = y0 - e * (H / 2 - 8); i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py) })
        ctx.stroke()
        const last = hist.length ? hist[hist.length - 1] : 0
        ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc((hist.length / 220) * W, y0 - last * (H / 2 - 8), 5, 0, Math.PI * 2); ctx.fill()
      }
      graphRaf.current = requestAnimationFrame(loop)
    }
    graphRaf.current = requestAnimationFrame(loop)
    return () => { if (graphRaf.current) cancelAnimationFrame(graphRaf.current) }
  }, [])

  return (
    <div className="rounded-2xl border border-teal-200 bg-white p-4 my-3">
      <h4 className="font-bold text-teal-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className={`rounded-lg overflow-hidden border border-gray-100 ${manual ? 'cursor-ew-resize' : ''}`} style={{ height: 300, background: 'radial-gradient(circle at 50% 40%, #ffffff, #eef2f7)' }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
        <Canvas camera={{ position: [3.5, 3, 5.5], fov: 50 }} onCreated={touch}>
          <GeneratorScene omega={manual ? 0 : (running ? omega : 0)} angleRef={angleRef} eRef={eRef} histRef={histRef} manual={manual} />
        </Canvas>
      </div>
      <canvas ref={gcv} width={660} height={90} className="w-full mt-2 rounded-lg border border-gray-100 bg-slate-50" />
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <button onClick={() => { touch(); setManual((m) => !m) }} className="px-4 py-2 rounded-lg bg-teal-600 text-white font-bold hover:bg-teal-700">{manual ? '↻ Tự động' : '✋ Quay'}</button>
        {manual ? (
          <label className="flex items-center gap-2 text-sm flex-1"><span className="w-28 text-gray-600">Góc quay khung</span>
            <input type="range" min={0} max={360} step={2} value={Math.round(manualDeg)} onChange={(e) => { touch(); const d = Number(e.target.value); setManualDeg(d); angleRef.current = (d * Math.PI) / 180 }} className="flex-1 accent-teal-500" />
            <span className="w-12 text-right font-semibold">{Math.round(manualDeg)}°</span></label>
        ) : (
          <label className="flex items-center gap-2 text-sm flex-1"><span className="w-28 text-gray-600">Tốc độ quay ω</span>
            <input type="range" min={0.5} max={5} step={0.5} value={omega} onChange={(e) => { touch(); setOmega(Number(e.target.value)) }} className="flex-1" />
            <span className="w-10 text-right font-semibold">{omega.toFixed(1)}</span></label>
        )}
        {!manual && <button onClick={() => setRunning((r) => !r)} className="px-4 py-2 rounded-lg border border-teal-200 text-teal-700 font-bold hover:bg-teal-50">{running ? 'Tạm dừng' : 'Quay'}</button>}
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Bấm <b>"Quay"</b> rồi chỉnh <b>thanh trượt Góc quay khung</b> (hoặc kéo trái/phải trên hình) để tự quay khung; hoặc để <b>Tự động</b> quay đều. 🟢 B (N→S) · mũi tên = chiều dòng điện (đảo khi e đổi dấu). e = 0 khi mặt khung ⊥ B (Φ cực đại); |e| cực đại khi mặt khung ∥ B.</p>
    </div>
  )
}
export default AcGeneratorSimulation
